<template>
  <section class="UserList">
    <div v-if="displayUsersList.length !== 0 || isListLoading">
      <div class="UserList__tableDiv">
        <ScrollBox always>
          <Table class="UserList__table">
            <thead>
              <tr>
                <th class="UserList__head" scope="col">
                  <CheckBox
                    v-if="!fromDialog"
                    id="userItems__checkAll"
                    name="userItems__checkAll"
                    :aria-label="messages.common.selectAll"
                    :value="isThChecked"
                    :isChecked="isThChecked"
                    @change="onCheckHeader($event)"
                  />
                </th>
                <th
                  v-for="header in headList"
                  :key="header.key"
                  class="UserList__head"
                  scope="col"
                >
                  <div class="UserList__headInner">
                    <span class="UserList__label">{{ header.label }}</span>
                    <SortButton
                      v-if="header.key === 'name'"
                      class="UserList__sortButton"
                      :order="sortState.name.sortOrder"
                      :priority="sortState.name.sortPriority"
                      @click="toggleSort('name')"
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-if="!isListLoading">
                <tr
                  v-for="(item, index) in displayUsersList"
                  :key="index"
                  :class="{ [`UserList__row--check`]: item.isChecked }"
                >
                  <td class="UserList__tableItem">
                    <CheckBox
                      v-if="!item.isHideCheckBox"
                      :id="`${item.user.email ?? ''}__check`"
                      :name="`${item.user.email ?? ''}__check`"
                      :aria-label="messages.common.select"
                      :value="item.isChecked"
                      :isChecked="item.isChecked"
                      :isMinus="item.isMinus"
                      :isDisabled="item.isMinus"
                      @change="onCheckItem($event, item)"
                    />
                  </td>
                  <!-- Loop through headList to generate corresponding data in each row -->
                  <td
                    v-for="header in headList"
                    :key="header.key"
                    :class="[
                      'UserList__tableItem',
                      {
                        'UserList__tableItem--active':
                          header.key == 'name' &&
                          item.status === 'active' &&
                          isViewActiveUser,
                      },
                      {
                        'UserList__tableItem--status': header.key === 'status',
                      },
                    ]"
                    :title="getTooltipText(header, item)"
                    @click="
                      header.key == 'name' &&
                      item.status === 'active' &&
                      isViewActiveUser
                        ? openSideContents('VIEW', index)
                        : null
                    "
                  >
                    <template v-if="header.key === 'status' && !fromDialog">
                      <StatusItem
                        :status="item.status ?? ''"
                        :mailResendNeeded="mailResendNeeded"
                        @onClick="onClickReSendInvitation(item.user.email)"
                      />
                    </template>
                    <template v-else-if="header.key === 'authority'">
                      {{ item.user[header.key].displayName }}
                    </template>
                    <template v-else>
                      <!-- Dynamically access the value based on the header key -->
                      {{ item.user[header.key] }}
                    </template>
                  </td>
                </tr>
              </template>
              <template v-else>
                <tr
                  v-for="skeleton in skeletonCount"
                  :key="skeleton"
                  class="UserList__skeletonWrapper"
                >
                  <td class="UserList__skeleton" :colspan="colSpan">
                    <TableSkeleton />
                  </td>
                </tr>
              </template>
            </tbody>
          </Table>
        </ScrollBox>
      </div>
    </div>
    <div
      v-else-if="displayUsersList.length == 0 && !isListLoading"
      class="UserList__noData"
    >
      <NoData :text="noDataText" :img-url="noDataImgUrl" />
    </div>
  </section>
</template>

<script setup lang="ts">
import CheckBox from '@spectee/components/Atoms/CheckBox/index.vue';
import Table from '@spectee/components/Atoms/Table/index.vue';
import StatusItem from '@spectee/components/Molecules/StatusItem/index.vue';
import TableSkeleton from '@spectee/components/Molecules/TableSkeleton/index.vue';
import NoData from '@spectee/components/Molecules/NoData/index.vue';
import SortButton from '@spectee/components/Molecules/SortButton/index.vue';
import useAnswerSortItem from '@spectee/composables/useAnswerSortItem';
import { ref, watch, computed } from 'vue';
import noDataImage from '@spectee/assets/images/img_notFound.png';
import { SortState, HeadItem, UserItem } from '@spectee/types/atoms';
import messages from '../../../../messages.json';

defineExpose({
  name: 'UserList',
});

interface Props {
  displayUsers: UserItem[];
  isListLoading: boolean;
  headList: HeadItem[];
  isThChecked: boolean;
  fromDialog?: boolean;
  mailResendNeeded?: boolean;
  isViewActiveUser?: boolean;
  noDataText?: string;
  noDataImgUrl?: string;
}

const props = withDefaults(defineProps<Props>(), {
  fromDialog: false,
  isThChecked: false,
  noDataText: messages.user.info.noDataText,
  noDataImgUrl: noDataImage,
});

const trackedItems = ref<UserItem[]>([]);
const skeletonCount = ref(props.fromDialog ? 3 : 12);
const colSpan = ref(props.headList.length + 1);

const emit = defineEmits([
  'onCheckHeader',
  'onCheckItem',
  'openSideContents',
  'onClickReSendInvitation',
]);

watch(
  () => props.displayUsers,
  () => {
    trackedItems.value = props.displayUsers ? [...props.displayUsers] : [];
  }
);

const onCheckHeader = ($event: Event) => {
  emit('onCheckHeader', $event);
};

const onCheckItem = ($event: Event, item: UserItem) => {
  emit('onCheckItem', $event, item);
};

const openSideContents = (screenMode: string, index: number) => {
  emit('openSideContents', screenMode, index);
};

const onClickReSendInvitation = (email: string) => {
  emit('onClickReSendInvitation', email);
};

// Used to maintain the sort order (ascending-0 <-> Descending-1 <-> default-2)
const sortState = ref<SortState>({
  name: {
    sortOrder: 0,
    sortPriority: 0,
  },
});

/**
 * Sorting action / ソートの処理
 */
const { toggleSort, sortedItems } = useAnswerSortItem<any>(
  sortState,
  trackedItems
);

const displayUsersList = computed(() => {
  return sortedItems.value;
});

const getTooltipText = (header: HeadItem, item: UserItem) => {
  const userDetails = item.user as any;

  if (header.key === 'authority') {
    return userDetails[header.key]?.displayName || '';
  }
  if (header.key === 'status') {
    return '';
  }
  // For other columns, return raw value (as string)
  const userValue = userDetails[header.key];
  return userValue != null ? String(userValue) : '';
};
</script>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_pageForward.scss' as *;
.UserList {
  display: flex;
  height: auto;
  min-height: $service-unit * 50;
  overflow: auto;

  &__tableItem--active {
    cursor: pointer;
    color: $color-blue-60;
  }

  &__tableItem {
    @include table-item;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:nth-child(2),
    &:nth-child(3) {
      @include ellipsis;
    }
  }

  &__tableItem--status {
    overflow: unset;
  }

  &__row--check &__tableItem {
    background-color: $color-gray-20;
  }

  &__skeletonWrapper:not(:first-child) &__skeleton {
    padding-top: $service-unit * 6;
  }

  &__head {
    @include table-head;
    position: sticky;
    top: 0;
    z-index: 2;

    &:first-child {
      width: $service-unit * 11;
    }
  }

  &__headInner {
    align-items: center;
    gap: $service-unit;
    white-space: nowrap;
  }

  &__sortButton,
  &__headInner {
    display: inline-flex;
    vertical-align: middle;
    line-height: 1;
  }

  &__noData {
    height: 100%;
    width: 100%;
  }

  &__table {
    min-width: $service-unit * 240; //fixed for license dialog
  }

  @media (max-width: 767px) {
    &__head {
      font-size: $service-font-size-mp;
    }

    &__tableItem {
      font-size: $service-font-size-pp;
    }
  }
}
</style>
